/**
 * In-app web browser (Chrome-style): tab strip, back/forward/reload/home,
 * address bar with search, bookmark star (name + folder popover), bookmark bar
 * with folders, files bar (pinned Files-tab items for one-click upload into
 * sites), find-in-page (Ctrl/Cmd+F), ⋮ menu (new tab, incognito, history,
 * delete browsing data, downloads, bookmarks, recent tabs, zoom, desktop site,
 * settings). Pages load through our proxy (/api/browser/proxy) so the app keeps
 * its own cache + cookie jar and navigation stays inside the tab; sites that
 * refuse get "open in a real tab". Bookmarks, history, recent tabs, downloads,
 * pinned files and the encrypted login vault are shared between everyone who
 * has the browser enabled.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, RotateCw, Home, Star, Plus, X, MoreVertical, KeyRound, ExternalLink, Lock, VenetianMask } from "lucide-react";
import api from "@/lib/api";
import NewTabPage from "./NewTabPage";
import { BrowserMenu, BrowserPanel } from "./BrowserPanels";
import { FolderChip, BookmarkPopover } from "./BookmarkFolders";
import { FilesBar, FilePickerDialog } from "./FilesBar";
import FindBar from "./FindBar";
import { PENDING_KEY } from "./OpenInBrowser";

const ENGINE_NAME = { google: "Google", duckduckgo: "DuckDuckGo", bing: "Bing" };
const SEARCH = { google: "https://www.google.com/search?q=", duckduckgo: "https://duckduckgo.com/html/?q=", bing: "https://www.bing.com/search?q=" };
const newTab = (incognito = false) => ({ id: Math.random().toString(36).slice(2, 10), url: "", title: incognito ? "New Incognito tab" : "New tab", stack: [], idx: -1, incognito, profile: incognito ? `incog-${Math.random().toString(36).slice(2, 8)}` : "shared", nonce: 0 });
const host = (u) => { try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return ""; } };
export const toUrl = (text, engine = "duckduckgo") => {
  const t = (text || "").trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t;
  if (/^[\w.-]+\.[a-z]{2,}(\/.*)?$/i.test(t) && !t.includes(" ")) return `https://${t}`;
  return (SEARCH[engine] || SEARCH.duckduckgo) + encodeURIComponent(t);
};
const proxyUrl = (url, tab, desktop) => `/api/browser/proxy?url=${encodeURIComponent(url)}&p=${tab.profile}&d=${desktop ? 1 : 0}&n=${tab.nonce}`;
// Our own portal is a single-page app — through the proxy its router sees the proxy
// address and renders nothing, so it loads directly (same origin, no proxy needed).
const isOwnHost = (u) => { try { return new URL(u).host.replace(/^www\./, "") === window.location.host.replace(/^www\./, ""); } catch { return false; } };

export default function BrowserTab() {
  const [tabs, setTabs] = useState(() => { try { const s = JSON.parse(localStorage.getItem("elite-browser-tabs") || "null"); return s?.length ? s.filter((t) => !t.incognito) : [newTab()]; } catch { return [newTab()]; } });
  const [activeId, setActiveId] = useState(() => tabs[0].id);
  const [settings, setSettings] = useState({ search_engine: "duckduckgo", zoom: 100, desktop_site: false, show_bookmark_bar: true, show_files_bar: true, save_history: true, offer_save_logins: true });
  const [bookmarks, setBookmarks] = useState([]);
  const [folders, setFolders] = useState([]);
  const [pins, setPins] = useState([]);
  const [addr, setAddr] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [bmPop, setBmPop] = useState(false);
  const [panel, setPanel] = useState(null); // history | bookmarks | downloads | recent | logins | settings | clear
  const [logins, setLogins] = useState([]);
  const [stalled, setStalled] = useState(false);
  const [closed, setClosed] = useState([]);
  const [find, setFind] = useState(false);
  const [findResult, setFindResult] = useState({ count: 0, index: -1 });
  const [filePick, setFilePick] = useState(null); // {fid, multiple, accept} from the page
  const pickPreRef = useRef([]);
  const frameRef = useRef(null);
  const loadedRef = useRef(false);
  const tab = tabs.find((t) => t.id === activeId) || tabs[0];

  const patchTab = useCallback((id, patch) => setTabs((ts) => ts.map((t) => (t.id === id ? { ...t, ...(typeof patch === "function" ? patch(t) : patch) } : t))), []);
  const loadBookmarks = useCallback(async () => { try { const r = await api.get("/browser/bookmarks"); setBookmarks(r.data.bookmarks); setFolders(r.data.folders || []); } catch { /* not enabled */ } }, []);
  const loadPins = useCallback(async () => { try { setPins((await api.get("/browser/files")).data.files); } catch { /* not enabled */ } }, []);
  useEffect(() => { loadBookmarks(); loadPins(); api.get("/browser/settings").then((r) => setSettings(r.data)).catch(() => {}); api.get("/browser/tabs").then((r) => setClosed(r.data.tabs)).catch(() => {}); }, [loadBookmarks, loadPins]);
  useEffect(() => { localStorage.setItem("elite-browser-tabs", JSON.stringify(tabs.filter((t) => !t.incognito).map((t) => ({ ...t, nonce: 0 })))); }, [tabs]);
  useEffect(() => { setAddr(tab?.url || ""); setStalled(false); setFind(false); setFindResult({ count: 0, index: -1 }); setFilePick(null); loadedRef.current = false; }, [tab?.id, tab?.url, tab?.nonce]);

  // Fill-able logins for the current site
  useEffect(() => {
    const h = host(tab?.url || "");
    if (!h || tab?.incognito) { setLogins([]); return; }
    api.get("/browser/logins", { params: { host: h, reveal: true } }).then((r) => setLogins(r.data.logins)).catch(() => setLogins([]));
  }, [tab?.url, tab?.incognito]);

  // "site refuses to load" detector: no 'loaded' message within 9s
  useEffect(() => {
    if (!tab?.url) return undefined;
    const t = setTimeout(() => { if (!loadedRef.current) setStalled(true); }, 9000);
    return () => clearTimeout(t);
  }, [tab?.url, tab?.nonce, tab?.id]);

  const navigate = useCallback((id, url, { push = true } = {}) => {
    if (!url) return;
    patchTab(id, (t) => {
      const stack = push ? [...t.stack.slice(0, t.idx + 1), url] : t.stack;
      return { url, title: host(url) || url, stack, idx: push ? stack.length - 1 : t.idx, nonce: t.nonce + 1 };
    });
  }, [patchTab]);
  const openTab = useCallback((incognito = false, url = "") => { const t = newTab(incognito); setTabs((ts) => [...ts, t]); setActiveId(t.id); if (url) setTimeout(() => navigate(t.id, url), 0); setMenuOpen(false); }, [navigate]);

  // A vendor/customer globe button handed us a site to open (works whether we were mounted or not)
  useEffect(() => {
    const take = () => { const u = sessionStorage.getItem(PENDING_KEY); if (u) { sessionStorage.removeItem(PENDING_KEY); openTab(false, u); } };
    take();
    window.addEventListener(PENDING_KEY, take);
    return () => window.removeEventListener(PENDING_KEY, take);
  }, [openTab]);

  // Ctrl/Cmd+F while the browser tab is on screen → our find bar (the page forwards it too)
  useEffect(() => {
    const h = (e) => { if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === "f" && tab?.url) { e.preventDefault(); setFind(true); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [tab?.url]);

  // Messages from the proxied page
  useEffect(() => {
    const onMsg = (e) => {
      const d = e.data || {};
      if (!d.__eliteBrowser || e.source !== frameRef.current?.contentWindow) return;
      if (d.type === "navigate") navigate(tab.id, d.url);
      if (d.type === "open_real") window.open(d.url, "_blank", "noopener");
      if (d.type === "loaded") {
        loadedRef.current = true; setStalled(false);
        patchTab(tab.id, { title: d.title || host(d.url) });
        if (!tab.incognito && settings.save_history) api.post("/browser/history", { url: d.url, title: d.title }).catch(() => {});
      }
      if (d.type === "credentials" && !tab.incognito && settings.offer_save_logins && d.password) {
        const h = host(d.url);
        if (logins.some((l) => l.username === d.username)) return;
        toast(`Save login for ${h}?`, { description: d.username, duration: 12000, action: { label: "Save", onClick: () => api.post("/browser/logins", { url: d.url, username: d.username, password: d.password }).then(() => toast.success(`Login for ${h} saved to the shared vault`)) } });
      }
      if (d.type === "find_result") setFindResult({ count: d.count, index: d.index });
      if (d.type === "find_open") setFind(true);
      if (d.type === "find_close") setFind(false);
      if (d.type === "file_pick") setFilePick({ fid: d.fid, multiple: d.multiple, accept: d.accept });
      if (d.type === "file_pick_none") toast("This page has no upload box", { description: "Click the site's upload / choose-file button first, then pick from the files bar." });
      if (d.type === "files_set") { if (d.count) toast.success(`${d.count} file${d.count === 1 ? "" : "s"} handed to the site`); else toast.error("The site didn't accept the files — try its upload button again"); }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [tab, navigate, patchTab, settings, logins]);

  const go = (text) => { const u = toUrl(text, settings.search_engine); if (u) navigate(tab.id, u); };
  const back = () => tab.idx > 0 && patchTab(tab.id, (t) => ({ idx: t.idx - 1, url: t.stack[t.idx - 1], nonce: t.nonce + 1 }));
  const fwd = () => tab.idx < tab.stack.length - 1 && patchTab(tab.id, (t) => ({ idx: t.idx + 1, url: t.stack[t.idx + 1], nonce: t.nonce + 1 }));
  const reload = () => patchTab(tab.id, (t) => ({ nonce: t.nonce + 1 }));
  const home = () => patchTab(tab.id, { url: "", title: tab.incognito ? "New Incognito tab" : "New tab" });
  const closeTab = (id) => {
    const t = tabs.find((x) => x.id === id);
    if (t?.url && !t.incognito) { const entry = { url: t.url, title: t.title, closed_at: new Date().toISOString() }; const next = [entry, ...closed].slice(0, 50); setClosed(next); api.put("/browser/tabs", { tabs: next }).catch(() => {}); }
    setTabs((ts) => { const rest = ts.filter((x) => x.id !== id); const out = rest.length ? rest : [newTab()]; if (id === activeId) setActiveId(out[Math.max(0, Math.min(out.length - 1, ts.findIndex((x) => x.id === id)))].id); return out; });
  };
  const current = useMemo(() => bookmarks.find((b) => b.url === tab?.url), [bookmarks, tab?.url]);
  const starClick = async () => {
    if (!tab?.url) return;
    if (!current) await api.post("/browser/bookmarks", { url: tab.url, title: tab.title });
    await loadBookmarks();
    setBmPop(true);
  };
  const saveSettings = async (patch) => { const r = await api.put("/browser/settings", patch); setSettings(r.data); };
  const fill = (l) => { frameRef.current?.contentWindow?.postMessage({ __eliteFill: true, username: l.username, password: l.password }, "*"); toast.success(`Filled ${l.username}`); };
  const pickFromBar = (p) => {
    if (!tab?.url) { toast("Open the site first", { description: "Then click a pinned file to upload it there." }); return; }
    pickPreRef.current = [p.pin_id];
    frameRef.current?.contentWindow?.postMessage({ __eliteFindFileInput: true }, "*");
  };
  const sendFiles = (files) => { frameRef.current?.contentWindow?.postMessage({ __eliteFiles: true, fid: filePick?.fid, files }, "*"); setFilePick(null); pickPreRef.current = []; };
  const zoom = settings.zoom || 100;
  const barItems = bookmarks.filter((b) => !b.folder && b.on_bar !== false);
  const navBtn = "rounded-full p-1.5 text-white/70 hover:bg-white/10 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent";

  // Fill exactly the visible area under the header/tabs: the browser chrome never
  // scrolls off-screen; only the page inside the iframe scrolls.
  const rootRef = useRef(null);
  const [fillH, setFillH] = useState(null);
  useEffect(() => {
    const measure = () => {
      const el = rootRef.current;
      const sc = document.getElementById("dashboard-scroll");
      if (!el || !sc) return;
      const scRect = sc.getBoundingClientRect();
      const padB = (parseFloat(getComputedStyle(sc).paddingBottom) || 0) + (parseFloat(getComputedStyle(el.parentElement).paddingBottom) || 0);
      const top = el.getBoundingClientRect().top + sc.scrollTop - scRect.top;
      const vv = window.visualViewport;
      const visibleBottom = vv ? Math.min(scRect.bottom, vv.offsetTop + vv.height) : scRect.bottom;
      const h = Math.max(320, Math.floor(visibleBottom - scRect.top - top - padB));
      setFillH((prev) => (prev === h ? prev : h));
    };
    measure();
    window.addEventListener("resize", measure);
    window.visualViewport?.addEventListener("resize", measure);
    // header/banners (workgroup bar, tabs) settle after first paint — follow every layout change
    const ro = new ResizeObserver(measure);
    [document.body, document.getElementById("app-header"), document.getElementById("dashboard-scroll"), rootRef.current?.parentElement].forEach((n) => n && ro.observe(n));
    const t = setTimeout(measure, 300);
    const t2 = setTimeout(measure, 1200);
    return () => { window.removeEventListener("resize", measure); window.visualViewport?.removeEventListener("resize", measure); ro.disconnect(); clearTimeout(t); clearTimeout(t2); };
  }, []);

  return (
    <div ref={rootRef} style={fillH ? { height: fillH } : undefined} className="flex h-[calc(100vh-11rem)] flex-col overflow-hidden rounded-lg border border-white/10 bg-white/[0.02]" data-testid="browser-tab">
      {/* tab strip */}
      <div className="flex items-end gap-1 overflow-x-auto border-b border-white/10 px-2 pt-2" data-testid="browser-tabstrip">
        {tabs.map((t) => (
          <div key={t.id} onClick={() => setActiveId(t.id)} data-testid={`browser-tab-${t.id}`}
            className={`group flex max-w-[12rem] min-w-[7rem] cursor-pointer items-center gap-1.5 rounded-t-lg px-3 py-1.5 text-xs ${t.id === activeId ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/5"}`}>
            {t.incognito ? <VenetianMask className="h-3.5 w-3.5 shrink-0" /> : t.url ? <img alt="" className="h-3.5 w-3.5 shrink-0 rounded-sm" src={`https://www.google.com/s2/favicons?domain=${host(t.url)}&sz=32`} /> : <Home className="h-3.5 w-3.5 shrink-0" />}
            <span className="truncate">{t.title}</span>
            <button type="button" onClick={(e) => { e.stopPropagation(); closeTab(t.id); }} aria-label="Close tab" data-testid={`browser-close-${t.id}`} className="ml-auto rounded-full p-0.5 opacity-60 hover:bg-white/15 hover:opacity-100"><X className="h-3 w-3" /></button>
          </div>
        ))}
        <button type="button" onClick={() => openTab(false)} aria-label="New tab" data-testid="browser-new-tab" className="mb-1 rounded-full p-1.5 text-white/70 hover:bg-white/10 hover:text-white"><Plus className="h-4 w-4" /></button>
      </div>

      {/* nav bar */}
      <div className="flex items-center gap-1 px-2 py-1.5" data-testid="browser-navbar">
        <button type="button" onClick={back} disabled={!tab || tab.idx <= 0} aria-label="Back" data-testid="browser-back" className={navBtn}><ArrowLeft className="h-4 w-4" /></button>
        <button type="button" onClick={fwd} disabled={!tab || tab.idx >= tab.stack.length - 1} aria-label="Forward" data-testid="browser-forward" className={navBtn}><ArrowRight className="h-4 w-4" /></button>
        <button type="button" onClick={reload} disabled={!tab?.url} aria-label="Reload" data-testid="browser-reload" className={navBtn}><RotateCw className="h-4 w-4" /></button>
        <button type="button" onClick={home} aria-label="Home" data-testid="browser-home" className={navBtn}><Home className="h-4 w-4" /></button>
        <form className="flex min-w-0 flex-1 items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 focus-within:border-[#1a3fd1]" onSubmit={(e) => { e.preventDefault(); go(addr); }}>
          {tab?.url?.startsWith("https://") ? <Lock className="h-3.5 w-3.5 shrink-0 text-white/50" /> : tab?.incognito ? <VenetianMask className="h-3.5 w-3.5 shrink-0 text-white/50" /> : null}
          <input value={addr} onChange={(e) => setAddr(e.target.value)} onFocus={(e) => e.target.select()} placeholder={`Search ${ENGINE_NAME[settings.search_engine] || "DuckDuckGo"} or type a URL`} data-testid="browser-address"
            className="min-w-0 flex-1 bg-transparent font-mono text-xs text-white outline-none placeholder:text-white/40" />
          {logins.length > 0 && (
            <button type="button" onClick={() => fill(logins[0])} title={`Fill saved login: ${logins[0].username}`} data-testid="browser-fill-login"
              className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#1a3fd1] px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-[#1a3fd1]/85"><KeyRound className="h-3 w-3" /> Fill {logins[0].username}</button>
          )}
          {tab?.url && (
            <span className="relative flex shrink-0">
              <button type="button" onClick={starClick} aria-label="Bookmark" data-testid="browser-star" className="text-white/60 hover:text-white"><Star className={`h-4 w-4 ${current ? "fill-[#f5c451] text-[#f5c451]" : ""}`} /></button>
              {bmPop && current && <BookmarkPopover bookmark={current} folders={folders} onClose={() => setBmPop(false)} onChanged={loadBookmarks} />}
            </span>
          )}
        </form>
        <div className="relative">
          <button type="button" onClick={() => setMenuOpen((v) => !v)} aria-label="Menu" data-testid="browser-menu-btn" className={navBtn}><MoreVertical className="h-4 w-4" /></button>
          {menuOpen && (
            <BrowserMenu onClose={() => setMenuOpen(false)} settings={settings} saveSettings={saveSettings} tab={tab}
              onNewTab={() => openTab(false)} onIncognito={() => openTab(true)} onPanel={(p) => { if (p === "find") setFind(true); else setPanel(p); setMenuOpen(false); }}
              onOpenReal={() => tab?.url && window.open(tab.url, "_blank", "noopener")} />
          )}
        </div>
      </div>

      {/* bookmark bar: folders first, then loose bookmarks */}
      {settings.show_bookmark_bar && (folders.length > 0 || barItems.length > 0) && (
        <div className="flex items-center gap-1 overflow-x-auto border-b border-white/10 px-2 pb-1.5" data-testid="browser-bookmark-bar">
          {folders.map((f) => <FolderChip key={f} name={f} items={bookmarks.filter((b) => b.folder === f)} onOpen={(u) => navigate(tab.id, u)} testid={`browser-bar-folder-${f}`} />)}
          {barItems.map((b) => (
            <button key={b.bookmark_id} type="button" onClick={() => navigate(tab.id, b.url)} title={b.url} data-testid={`browser-bar-${b.bookmark_id}`}
              className="flex max-w-[10rem] shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-[11px] text-white/70 hover:bg-white/10 hover:text-white">
              <img alt="" className="h-3.5 w-3.5 rounded-sm" src={b.favicon} /><span className="truncate">{b.title}</span>
            </button>
          ))}
        </div>
      )}
      {settings.show_files_bar !== false && pins.length > 0 && <FilesBar pins={pins} onPick={pickFromBar} onRemoved={loadPins} />}

      {/* content */}
      <div className="relative min-h-0 flex-1 overflow-hidden rounded-b-lg bg-white" data-testid="browser-content">
        {!tab?.url ? (
          <NewTabPage bookmarks={bookmarks} folders={folders} incognito={tab?.incognito} engine={settings.search_engine} onGo={go} onOpen={(u) => navigate(tab.id, u)} onChanged={loadBookmarks} />
        ) : (
          <>
            <iframe key={`${tab.id}-${tab.nonce}`} ref={frameRef} title="page" src={proxyUrl(tab.url, tab, settings.desktop_site)} data-testid="browser-frame"
              sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-downloads allow-modals"
              style={{ width: `${10000 / zoom}%`, height: `${10000 / zoom}%`, transform: `scale(${zoom / 100})`, transformOrigin: "0 0", border: 0 }} />
            {find && <FindBar frameRef={frameRef} result={findResult} onClose={() => setFind(false)} />}
            {stalled && (
              <div className="absolute inset-x-0 top-0 flex flex-wrap items-center justify-between gap-2 bg-[#1a3fd1] px-3 py-2 text-xs text-white" data-testid="browser-stalled">
                <span>This site may refuse to load inside the app (it blocks embedding or needs a Google/bank login).</span>
                <span className="flex gap-2">
                  <button type="button" onClick={() => window.open(tab.url, "_blank", "noopener")} data-testid="browser-open-real" className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-1 font-semibold text-[#1a3fd1]"><ExternalLink className="h-3 w-3" /> Open in a real tab</button>
                  <button type="button" onClick={() => setStalled(false)} className="rounded-md border border-white/40 px-2 py-1">Keep waiting</button>
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {filePick && <FilePickerDialog request={filePick} pins={pins} preselect={pickPreRef.current} onCancel={() => { setFilePick(null); pickPreRef.current = []; }} onSubmit={sendFiles} />}
      {panel && <BrowserPanel kind={panel} onClose={() => setPanel(null)} settings={settings} saveSettings={saveSettings} bookmarks={bookmarks} folders={folders} pins={pins} onChanged={() => { loadBookmarks(); loadPins(); }}
        closedTabs={closed} onOpen={(u) => { navigate(tab.id, u); setPanel(null); }} onOpenNew={(u) => { openTab(false, u); setPanel(null); }} />}
    </div>
  );
}
